import { useEffect, useState } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { QrCode } from "lucide-react";

export default function OrderConfirmationPage() {
    const { id } = useParams();
    const location = useLocation();
    const [order, setOrder] = useState(location.state?.order || null);
    const [qr, setQr] = useState(null);

    useEffect(() => {
        if (!order) {
            api.get("/orders").then(({ data }) => {
                const found = data.find(o => o.id === id);
                if (found) setOrder(found);
            }).catch(() => {});
        }
    }, [id, order]);

    // Fetch the definitive SEPA QR for the order
    useEffect(() => {
        if (!order) return;
        if (order.payment_method !== "qr_code" && order.payment_method !== "bank_transfer") return;
        api.get(`/orders/${order.id}/payment-qr`)
            .then(({ data }) => setQr(data))
            .catch(() => setQr(null));
    }, [order]);

    if (!order) return <div className="p-10 text-zinc-500">Laden…</div>;

    return (
        <div data-testid="order-confirmation" className="bg-zinc-50 min-h-screen">
            <div className="max-w-3xl mx-auto px-6 py-16">
                <div className="border border-zinc-200 bg-white p-10">
                    <p className="label-tiny">Bestelling bevestigd</p>
                    <h1 className="font-display text-5xl mt-2">Dank je!</h1>
                    <p className="text-zinc-600 mt-2">Bestelnummer: <span className="font-mono-dm" data-testid="order-number">{order.order_number}</span></p>

                    {order.tracking_number && (
                        <div data-testid="order-tracking" className="mt-4 border border-blue-200 bg-blue-50 p-4 text-sm">
                            <p className="label-tiny text-blue-800 mb-1">📦 Track & Trace</p>
                            <p>Je bestelling is onderweg! Volg de pakket via:</p>
                            <a href={`https://track.bpost.cloud/btr/web/#/search?itemCode=${encodeURIComponent(order.tracking_number)}`}
                                target="_blank" rel="noopener noreferrer"
                                className="font-mono-dm text-blue-700 hover:underline mt-1 inline-block">
                                {order.tracking_number} →
                            </a>
                        </div>
                    )}

                    <div className="border-t border-zinc-200 mt-8 pt-6">
                        <p className="label-tiny mb-3 flex items-center gap-2"><QrCode size={14} /> Betaling — Scan & Pay</p>
                        {qr ? (
                            <div data-testid="order-qr-block" className="flex flex-col md:flex-row items-center gap-6">
                                <img src={qr.data_url} alt="SEPA QR Code" className="w-56 h-56 border border-zinc-100" data-testid="order-qr-image" />
                                <div className="flex-1 space-y-2 text-sm">
                                    <p>Open je bank-app (ING, KBC, BNP, Belfius, Argenta, …) → <strong>"Betalen"</strong> → <strong>"Scan QR-code"</strong></p>
                                    <p>Bedrag <strong>€ {order.total.toFixed(2)}</strong> en de mededeling worden automatisch ingevuld.</p>
                                    <div className="border border-zinc-200 bg-zinc-50 p-3 font-mono-dm text-xs space-y-1 mt-3">
                                        <p>IBAN: <strong>{qr.iban.replace(/(.{4})/g, "$1 ").trim()}</strong></p>
                                        <p>Begunstigde: <strong>{qr.beneficiary}</strong></p>
                                        <p>Mededeling: <strong>{order.order_number}</strong></p>
                                        <p>Bedrag: <strong>€ {order.total.toFixed(2)}</strong></p>
                                    </div>
                                    <p className="text-xs text-zinc-500 mt-2">Kan je de QR niet scannen? Vul de gegevens hierboven handmatig in je bank-app in.</p>
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm space-y-2">
                                <p>Maak <strong>€ {order.total.toFixed(2)}</strong> over naar:</p>
                                <p className="font-mono-dm">IBAN: BE16 0004 1120 9874<br/>Begunstigde: VdS Gift Shop</p>
                                <p>Mededeling: <strong>{order.order_number}</strong></p>
                            </div>
                        )}
                    </div>

                    <div className="border-t border-zinc-200 mt-8 pt-6">
                        <p className="label-tiny mb-3">Bestelde items</p>
                        <ul className="space-y-2 text-sm">
                            {order.items.map(it => (
                                <li key={it.id} className="flex justify-between">
                                    <span>{it.tshirt_name} · {it.tshirt_color_name} · {it.size} × {it.quantity}</span>
                                    <span className="font-mono-dm">€ {it.line_total.toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="border-t border-zinc-200 mt-4 pt-3 flex justify-between font-semibold">
                            <span>Totaal</span><span className="font-display text-2xl">€ {order.total.toFixed(2)}</span>
                        </div>
                    </div>

                    <Link to="/designer" className="btn-primary mt-8 inline-block" data-testid="order-back-designer">Nieuw ontwerp maken</Link>
                </div>
            </div>
        </div>
    );
}
